import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Copy, RefreshCw, ArrowRightLeft, Volume2 } from 'lucide-react';
import { toast } from 'sonner';

const MorseCodeTool = () => {
  const [text, setText] = useState('');
  const [morse, setMorse] = useState('');
  const [mode, setMode] = useState('encode');

  const morseCode = {
    'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.',
    'G': '--.', 'H': '....', 'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..',
    'M': '--', 'N': '-.', 'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.',
    'S': '...', 'T': '-', 'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-',
    'Y': '-.--', 'Z': '--..', '0': '-----', '1': '.----', '2': '..---',
    '3': '...--', '4': '....-', '5': '.....', '6': '-....', '7': '--...',
    '8': '---..', '9': '----.', '.': '.-.-.-', ',': '--..--', '?': '..--..',
    "'": '.----.', '!': '-.-.--', '/': '-..-.', '(': '-.--.', ')': '-.--.-',
    '&': '.-...', ':': '---...', ';': '-.-.-.', '=': '-...-', '+': '.-.-.',
    '-': '-....-', '_': '..--.-', '"': '.-..-.', '$': '...-..-', '@': '.--.-.',
    ' ': '/'
  };

  const reverseMorse = Object.fromEntries(
    Object.entries(morseCode).map(([k, v]) => [v, k])
  );

  const textToMorse = (input) => {
    return input.toUpperCase().split('').map(char => morseCode[char] || char).join(' ');
  };

  const morseToText = (input) => {
    return input.split(' ').map(code => {
      if (code === '/') return ' ';
      return reverseMorse[code] || code;
    }).join('');
  };

  const handleTextChange = (value) => {
    setText(value);
    setMorse(textToMorse(value));
  };

  const handleMorseChange = (value) => {
    setMorse(value);
    setText(morseToText(value));
  };

  const playMorse = () => {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const dotDuration = 0.1;
    let time = audioCtx.currentTime;

    morse.split('').forEach(char => {
      if (char === '.' || char === '-') {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.value = 600;
        osc.start(time);
        osc.stop(time + (char === '.' ? dotDuration : dotDuration * 3));
        time += (char === '.' ? dotDuration : dotDuration * 3) + dotDuration;
      } else if (char === ' ') {
        time += dotDuration * 2;
      } else if (char === '/') {
        time += dotDuration * 4;
      }
    });

    toast.success('Playing Morse code...');
  };

  const copyMorse = () => {
    navigator.clipboard.writeText(morse);
    toast.success('Morse code copied!');
  };

  const copyText = () => {
    navigator.clipboard.writeText(text);
    toast.success('Text copied!');
  };

  return (
    <div className="space-y-6" data-testid="morse-code-tool">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Text</label>
          <Textarea
            placeholder="Enter text to convert to Morse code..."
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            className="min-h-[150px] font-mono text-sm bg-muted/50"
          />
          <Button onClick={copyText} variant="outline" className="w-full mt-2" disabled={!text}>
            <Copy className="w-4 h-4 mr-2" /> Copy Text
          </Button>
        </div>
        <div>
          <label className="text-sm text-muted-foreground mb-2 block">Morse Code</label>
          <Textarea
            placeholder="Enter Morse code (dots, dashes, spaces)..."
            value={morse}
            onChange={(e) => handleMorseChange(e.target.value)}
            className="min-h-[150px] font-mono text-sm bg-muted/50"
          />
          <div className="flex gap-2 mt-2">
            <Button onClick={copyMorse} variant="outline" className="flex-1" disabled={!morse}>
              <Copy className="w-4 h-4 mr-2" /> Copy Morse
            </Button>
            <Button onClick={playMorse} variant="outline" disabled={!morse}>
              <Volume2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <h3 className="font-semibold mb-3">Morse Code Reference</h3>
        <div className="grid grid-cols-6 md:grid-cols-9 gap-2 text-xs font-mono">
          {Object.entries(morseCode).slice(0, 36).map(([char, code]) => (
            <div key={char} className="p-1 rounded bg-background text-center">
              <span className="block text-primary">{char === ' ' ? '␣' : char}</span>
              <span className="text-muted-foreground">{code}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 justify-center">
        <Button onClick={() => { setText(''); setMorse(''); }} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default MorseCodeTool;
